import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TRIGGER_OPTIONS = [
  { id: 'autopilot', label: 'Auto-Pilot Strategy', icon: '🤖' },
  { id: 'generate', label: 'Content Generation', icon: '🚀' },
  { id: 'multiplier', label: 'Content Multiplier', icon: '⚡' },
  { id: 'content-brief', label: 'Content Briefs', icon: '💡' },
  { id: 'apollo', label: "Apollo's Chamber", icon: '☀️' },
];

export const ZapierIntegration = () => {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [triggers, setTriggers] = useState(['autopilot', 'generate', 'multiplier', 'content-brief', 'apollo']);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [stats, setStats] = useState({ total_fired: 0, last_fired: null });
  const [history, setHistory] = useState([]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchConfig();
    fetchHistory();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/zapier/config`, { headers });
      const d = res.data;
      setWebhookUrl(d.webhook_url || '');
      setEnabled(d.enabled || false);
      setTriggers(d.triggers || []);
      setConfigured(d.configured || false);
      setStats({ total_fired: d.total_fired || 0, last_fired: d.last_fired });
    } catch (e) {
      console.error('Failed to fetch Zapier config:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/api/zapier/history?limit=10`, { headers });
      setHistory(res.data.history || []);
    } catch (e) {
      console.error('Failed to fetch history:', e);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/zapier/config`, {
        webhook_url: webhookUrl,
        enabled,
        triggers,
      }, { headers });
      setConfigured(true);
      setTestResult({ success: true, message: 'Configuration saved!' });
      setTimeout(() => setTestResult(null), 3000);
    } catch (e) {
      setTestResult({ success: false, message: 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  const testWebhook = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/api/zapier/test`, { webhook_url: webhookUrl }, { headers });
      setTestResult(res.data);
      fetchHistory();
    } catch (e) {
      setTestResult({ success: false, message: 'Test failed — check your webhook URL' });
    } finally {
      setTesting(false);
    }
  };

  const deleteConfig = async () => {
    try {
      await axios.delete(`${API}/api/zapier/config`, { headers });
      setWebhookUrl('');
      setEnabled(false);
      setConfigured(false);
      setStats({ total_fired: 0, last_fired: null });
      setTestResult({ success: true, message: 'Webhook disconnected' });
      setTimeout(() => setTestResult(null), 3000);
    } catch (e) {
      setTestResult({ success: false, message: 'Failed to disconnect' });
    }
  };

  const toggleTrigger = (id) => {
    setTriggers(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="zapier-integration">
      {/* Header */}
      <div className="flex items-center gap-4 mb-2">
        <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="w-6 h-6 text-orange-400" fill="currentColor">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
          </svg>
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Zapier Integration</h2>
          <p className="text-sm text-gray-400">Auto-distribute your content to 6,000+ apps</p>
        </div>
        {configured && (
          <span className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold ${enabled ? 'bg-green-500/15 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
            {enabled ? 'Connected' : 'Paused'}
          </span>
        )}
      </div>

      {/* Stats bar */}
      {configured && (
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-xl border border-gray-800 bg-gray-900/50">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Webhooks Fired</p>
            <p className="text-2xl font-bold text-white mt-1">{stats.total_fired}</p>
          </div>
          <div className="p-3 rounded-xl border border-gray-800 bg-gray-900/50">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Last Fired</p>
            <p className="text-sm font-medium text-white mt-2">
              {stats.last_fired ? new Date(stats.last_fired).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never'}
            </p>
          </div>
        </div>
      )}

      {/* Webhook URL */}
      <div className="p-5 rounded-xl border border-gray-800 bg-gray-900/50 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Webhook URL</label>
          <div className="flex gap-2">
            <input
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://hooks.zapier.com/hooks/catch/..."
              className="flex-1 px-4 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm placeholder-gray-500 focus:border-orange-500/50 focus:outline-none transition-colors"
              data-testid="webhook-url-input"
            />
            <button
              onClick={testWebhook}
              disabled={!webhookUrl || testing}
              className="px-4 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-gray-300 hover:text-orange-400 hover:border-orange-500/40 text-sm font-medium disabled:opacity-40 transition-all"
              data-testid="test-webhook-btn"
            >
              {testing ? 'Testing...' : 'Test'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Get your webhook URL from Zapier: Create a Zap → Trigger → Webhooks by Zapier → Catch Hook → Copy URL
          </p>
        </div>

        {/* Test result */}
        {testResult && (
          <div className={`p-3 rounded-lg text-sm ${testResult.success ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`} data-testid="test-result">
            {testResult.message}
          </div>
        )}

        {/* Enable toggle */}
        <div className="flex items-center justify-between pt-2">
          <div>
            <p className="text-sm font-medium text-white">Auto-send content</p>
            <p className="text-xs text-gray-500">Automatically fire webhooks when content is generated</p>
          </div>
          <button
            onClick={() => setEnabled(!enabled)}
            className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? 'bg-orange-500' : 'bg-gray-700'}`}
            data-testid="enable-toggle"
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : ''}`} />
          </button>
        </div>
      </div>

      {/* Triggers */}
      <div className="p-5 rounded-xl border border-gray-800 bg-gray-900/50 space-y-3">
        <h3 className="text-sm font-semibold text-white">Content Triggers</h3>
        <p className="text-xs text-gray-500">Choose which features send content to Zapier</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
          {TRIGGER_OPTIONS.map(opt => (
            <button
              key={opt.id}
              onClick={() => toggleTrigger(opt.id)}
              className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                triggers.includes(opt.id) 
                  ? 'border-orange-500/40 bg-orange-500/10 text-white' 
                  : 'border-gray-800 bg-gray-900/50 text-gray-500 hover:border-gray-700'
              }`}
              data-testid={`trigger-${opt.id}`}
            >
              <span className="text-lg">{opt.icon}</span>
              <span className="text-sm font-medium">{opt.label}</span>
              {triggers.includes(opt.id) && (
                <span className="ml-auto text-orange-400 text-xs">Active</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Save / Disconnect */}
      <div className="flex gap-3">
        <button
          onClick={saveConfig}
          disabled={!webhookUrl || saving}
          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold text-sm hover:from-orange-600 hover:to-amber-600 disabled:opacity-40 transition-all"
          data-testid="save-config-btn"
        >
          {saving ? 'Saving...' : configured ? 'Update Configuration' : 'Connect Zapier'}
        </button>
        {configured && (
          <button
            onClick={deleteConfig}
            className="px-6 py-3 rounded-xl border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/10 transition-all"
            data-testid="disconnect-btn"
          >
            Disconnect
          </button>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="p-5 rounded-xl border border-gray-800 bg-gray-900/50 space-y-3">
          <h3 className="text-sm font-semibold text-white">Recent Webhooks</h3>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-800/50 last:border-0">
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${h.status === 'delivered' ? 'bg-green-400' : 'bg-red-400'}`} />
                  <span className="text-sm text-gray-300">{h.payload_preview || h.event_type}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">
                    {new Date(h.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${h.status === 'delivered' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                    {h.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How it works */}
      {!configured && (
        <div className="p-5 rounded-xl border border-gray-800 bg-gray-900/30 space-y-3">
          <h3 className="text-sm font-semibold text-white">How it works</h3>
          <ol className="space-y-2 text-sm text-gray-400">
            <li className="flex gap-2"><span className="text-orange-400 font-bold">1.</span> Create a free Zapier account at zapier.com</li>
            <li className="flex gap-2"><span className="text-orange-400 font-bold">2.</span> Create a new Zap → Choose "Webhooks by Zapier" as trigger → "Catch Hook"</li>
            <li className="flex gap-2"><span className="text-orange-400 font-bold">3.</span> Copy the webhook URL and paste it above</li>
            <li className="flex gap-2"><span className="text-orange-400 font-bold">4.</span> Click "Test" to send sample data, then set up your Zap actions (post to LinkedIn, WordPress, etc.)</li>
            <li className="flex gap-2"><span className="text-orange-400 font-bold">5.</span> Enable auto-send and choose your triggers — done!</li>
          </ol>
        </div>
      )}
    </div>
  );
};
